/**
 * The Name-off's ranking: a binary insertion sort that asks only the comparisons it needs (about
 * 25 picks put 11 names in order) and reuses every answer already given, so it picks up where it
 * left off, in any tab. The answers are kept in the ratings record's `pairs` (state/app.ts).
 * Pure, so it can be tested.
 */
import type { NameEntry, SexIdx } from "../data/types";

/** "a|b" (keys in order) -> 1 when the first was preferred, -1 the second, 0 neither */
export type Pairs = Record<string, number>;

export function pairKey(a: string, b: string): string {
  return a < b ? `${a}|${b}` : `${b}|${a}`;
}

/** The answer for a pair as it was put: 1 for `a`, -1 for `b`, 0 for neither, undefined if never asked. */
export function preferred(pairs: Pairs, a: string, b: string): number | undefined {
  const v = pairs[pairKey(a, b)];
  if (v === undefined) return undefined;
  return a < b ? v : -v;
}

export interface Standing {
  /** names placed so far, favourite first (all of them once the Name-off is done) */
  ranked: string[];
  /** the next pair to ask about, or null when every name is placed */
  ask: [string, string] | null;
  /** how many names are placed */
  placed: number;
}

/** Where the Name-off has got to, for names in this order (the order they're placed in). */
export function standing(keys: string[], pairs: Pairs): Standing {
  const ranked: string[] = [];
  for (const k of keys) {
    let lo = 0;
    let hi = ranked.length;
    while (lo < hi) {
      const mid = (lo + hi) >> 1;
      const p = preferred(pairs, k, ranked[mid]);
      if (p === undefined) return { ranked, ask: [k, ranked[mid]], placed: ranked.length };
      // can't decide: the name already placed stays ahead
      if (p > 0) hi = mid;
      else lo = mid + 1;
    }
    ranked.splice(lo, 0, k);
  }
  return { ranked, ask: null, placed: ranked.length };
}

/** The most picks the Name-off can take for n names (binary insertion's worst case). */
export function mostPicks(n: number): number {
  let total = 0;
  for (let i = 1; i < n; i++) total += Math.ceil(Math.log2(i + 1));
  return total;
}

/** A shortlisted name belongs to the girls' or the boys' Name-off (unisex names to both). */
export function inGroup(e: NameEntry | undefined, group: SexIdx | null): boolean {
  if (group === null || !e) return true;
  return e.g === "B" || e.g === (group === 0 ? "F" : "M");
}
